// BirthChartScreen - Main screen for birth chart feature
// Brings together form, chart visualization, and interpretation
import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, RotateCcw, Save, Check, Stars } from 'lucide-react';
import { MarkdownText } from '../common/MarkdownText';
import { PageContainer } from '../layout/PageContainer';
import { Button } from '../common/Button';
import { PageLoader } from '../common/LoadingSpinner';
import { useBirthChart } from '../../hooks/useBirthChart';
import { useTone } from '../../hooks/useTone';
import { BirthDataForm } from '../birthchart/BirthDataForm';
import { NatalChartWheel } from '../birthchart/NatalChartWheel';
import { PlanetCard } from '../birthchart/PlanetCard';

export const BirthChartScreen = ({ currentUser }) => {
    const { promptPrefix } = useTone();
    const {
        // Form state
        birthDate, setBirthDate,
        birthTime, setBirthTime,
        birthLocation,
        unknownTime, setUnknownTime,
        locationResults,
        isSearchingLocation,
        searchLocation,
        selectLocation,

        // Chart data
        chartData,
        interpretation,

        // Save state
        isSaving,
        isSaved,

        // Loading/error
        isLoading,
        isLoadingInterpretation,
        error,
        setError,

        // Navigation
        currentStep,
        nextStep,
        prevStep,

        // Saved charts
        savedCharts,
        isLoadingSavedCharts,
        loadSavedCharts,
        loadSavedChart,

        // Actions
        calculateChart,
        getInterpretation,
        getPlanetInsight,
        saveChart,
        reset
    } = useBirthChart(currentUser, promptPrefix);

    // Load saved charts on mount for logged-in users
    React.useEffect(() => {
        if (currentUser && !currentUser.isAnonymous) {
            loadSavedCharts();
        }
    }, [currentUser, loadSavedCharts]);

    // Save modal state
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [chartNameInput, setChartNameInput] = useState('My Birth Chart');
    const [showSavedChartsPanel, setShowSavedChartsPanel] = useState(false);

    // Planet card state
    const [selectedPlanet, setSelectedPlanet] = useState(null);
    const [planetInsight, setPlanetInsight] = useState('');
    const [isLoadingPlanetInsight, setIsLoadingPlanetInsight] = useState(false);

    // Handle chart calculation
    const handleCalculate = useCallback(async () => {
        const data = await calculateChart();
        if (data) {
            // Auto-generate interpretation
            getInterpretation(data);
        }
    }, [calculateChart, getInterpretation]);

    // Handle planet click
    const handlePlanetClick = useCallback((planet) => {
        setSelectedPlanet(planet);
        setPlanetInsight('');
    }, []);

    // Handle getting planet insight
    const handleGetPlanetInsight = useCallback(async (planet) => {
        setIsLoadingPlanetInsight(true);
        const insight = await getPlanetInsight(planet);
        setPlanetInsight(insight || '');
        setIsLoadingPlanetInsight(false);
    }, [getPlanetInsight]);

    // Close planet card
    const closePlanetCard = useCallback(() => {
        setSelectedPlanet(null);
        setPlanetInsight('');
    }, []);

    // Handle save chart
    const handleSaveChart = useCallback(async () => {
        if (!currentUser || currentUser.isAnonymous) {
            setError('Please sign in to save your birth chart.');
            return;
        }
        const result = await saveChart(chartNameInput);
        if (result) {
            setShowSaveModal(false);
        }
    }, [currentUser, saveChart, chartNameInput, setError]);

    // Render form or chart
    const renderContent = () => {
        // Show loading state
        if (isLoading) {
            return (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex flex-col items-center justify-center min-h-[60vh]"
                >
                    <PageLoader text="Consulting the celestial sphere..." />
                    <motion.div
                        className="mt-6 text-purple-300/70 text-center max-w-xs"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1 }}
                    >
                        Calculating planetary positions for your unique cosmic blueprint...
                    </motion.div>
                </motion.div>
            );
        }

        // Show chart if we have data
        if (chartData) {
            return (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-8"
                >
                    {/* Header */}
                    <div className="text-center">
                        <motion.h1
                            className="text-3xl sm:text-4xl font-serif mb-2"
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <span className="bg-gradient-to-r from-amber-200 via-amber-100 to-amber-300 bg-clip-text text-transparent">
                                Your Birth Chart
                            </span>
                        </motion.h1>
                        <p className="text-purple-300/70">
                            {chartData.birthData.date} • {chartData.birthData.time}
                        </p>
                    </div>

                    {/* Chart visualization */}
                    <NatalChartWheel
                        chartData={chartData}
                        onPlanetClick={handlePlanetClick}
                        selectedPlanet={selectedPlanet?.name}
                    />

                    {/* Big Three summary */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="grid grid-cols-3 gap-3"
                    >
                        <BigThreeCard
                            title="Sun"
                            sign={chartData.planets.find(p => p.name === 'Sun')?.sign}
                            symbol={chartData.planets.find(p => p.name === 'Sun')?.signSymbol}
                            subtitle="Core Identity"
                        />
                        <BigThreeCard
                            title="Moon"
                            sign={chartData.planets.find(p => p.name === 'Moon')?.sign}
                            symbol={chartData.planets.find(p => p.name === 'Moon')?.signSymbol}
                            subtitle="Emotions"
                        />
                        <BigThreeCard
                            title="Rising"
                            sign={chartData.ascendant.sign}
                            symbol={chartData.ascendant.signSymbol}
                            subtitle="First Impression"
                        />
                    </motion.div>

                    {/* Interpretation */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        className="p-6 bg-theme-glass backdrop-blur-sm border border-theme-glass rounded-2xl"
                    >
                        <div className="flex items-center gap-2 text-theme-accent font-semibold mb-4">
                            <Sparkles className="w-5 h-5" />
                            Cosmic Interpretation
                        </div>

                        {isLoadingInterpretation ? (
                            <div className="space-y-3">
                                {[...Array(4)].map((_, i) => (
                                    <div
                                        key={i}
                                        className="h-4 bg-purple-700/30 rounded animate-pulse"
                                        style={{ width: `${85 - i * 10}%` }}
                                    />
                                ))}
                            </div>
                        ) : interpretation ? (
                            <MarkdownText size="base">
                                {interpretation}
                            </MarkdownText>
                        ) : (
                            <Button
                                variant="secondary"
                                onClick={() => getInterpretation()}
                                icon={Sparkles}
                            >
                                Generate Interpretation
                            </Button>
                        )}
                    </motion.div>

                    {/* Planetary positions table */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7 }}
                        className="p-6 bg-theme-glass backdrop-blur-sm border border-theme-glass rounded-2xl"
                    >
                        <h3 className="text-lg font-serif text-theme-secondary mb-4">Planetary Positions</h3>
                        <div className="space-y-2">
                            {chartData.planets.map((planet, i) => (
                                <motion.button
                                    key={planet.name}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.1 * i }}
                                    onClick={() => handlePlanetClick(planet)}
                                    className="w-full flex items-center justify-between p-3 bg-theme-glass hover:bg-theme-glass border border-theme-glass hover:border-theme-primary rounded-lg transition-all group"
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="text-xl">{planet.symbol}</span>
                                        <span className="text-theme">{planet.name}</span>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-theme-secondary text-sm">
                                            {planet.signSymbol} {planet.sign}
                                        </div>
                                        <div className="text-theme-muted text-xs">
                                            House {planet.house}
                                        </div>
                                    </div>
                                </motion.button>
                            ))}
                        </div>
                    </motion.div>

                    {/* Actions */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.9 }}
                        className="flex flex-wrap justify-center gap-3"
                    >
                        {/* Save button - only show for logged in users */}
                        {currentUser && !currentUser.isAnonymous && (
                            isSaved ? (
                                <Button
                                    variant="secondary"
                                    size="md"
                                    disabled
                                    icon={Check}
                                    className="border-green-500/30 text-green-400"
                                >
                                    Chart Saved!
                                </Button>
                            ) : (
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={() => setShowSaveModal(true)}
                                    icon={Save}
                                >
                                    Save Chart
                                </Button>
                            )
                        )}
                        <Button
                            variant="secondary"
                            size="md"
                            onClick={reset}
                            icon={RotateCcw}
                        >
                            New Chart
                        </Button>
                    </motion.div>
                </motion.div>
            );
        }

        // Show form
        return (
            <div className="min-h-[70vh] flex flex-col justify-center">
                {/* Title */}
                <motion.div
                    className="text-center mb-8"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <h1 className="text-3xl sm:text-4xl font-serif mb-2">
                        <span className="bg-gradient-to-r from-amber-200 via-amber-100 to-amber-300 bg-clip-text text-transparent">
                            Birth Star Chart
                        </span>
                    </h1>
                    <p className="text-purple-300/70">Discover your cosmic blueprint</p>
                </motion.div>

                {/* Error message */}
                {error && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-xl text-red-200 text-center"
                    >
                        {error}
                        <button
                            onClick={() => setError('')}
                            className="ml-3 text-red-300 hover:text-red-100"
                        >
                            ×
                        </button>
                    </motion.div>
                )}

                {/* Form */}
                <BirthDataForm
                    birthDate={birthDate}
                    setBirthDate={setBirthDate}
                    birthTime={birthTime}
                    setBirthTime={setBirthTime}
                    birthLocation={birthLocation}
                    unknownTime={unknownTime}
                    setUnknownTime={setUnknownTime}
                    locationResults={locationResults}
                    isSearchingLocation={isSearchingLocation}
                    searchLocation={searchLocation}
                    selectLocation={selectLocation}
                    currentStep={currentStep}
                    nextStep={nextStep}
                    prevStep={prevStep}
                    isLoading={isLoading}
                    onCalculate={handleCalculate}
                />

                {/* Saved Charts Section - for logged in users */}
                {currentUser && !currentUser.isAnonymous && savedCharts.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7 }}
                        className="mt-8 max-w-md mx-auto"
                    >
                        <button
                            onClick={() => setShowSavedChartsPanel(!showSavedChartsPanel)}
                            className="w-full flex items-center justify-between px-4 py-3 bg-purple-900/40 border border-purple-500/20 rounded-xl text-amber-200 hover:bg-purple-800/40 transition-colors"
                        >
                            <span className="flex items-center gap-2">
                                <Stars className="w-4 h-4" />
                                My Saved Charts ({savedCharts.length})
                            </span>
                            <motion.span
                                animate={{ rotate: showSavedChartsPanel ? 180 : 0 }}
                                transition={{ duration: 0.2 }}
                            >
                                ▼
                            </motion.span>
                        </button>

                        <AnimatePresence>
                            {showSavedChartsPanel && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="overflow-hidden"
                                >
                                    <div className="mt-2 space-y-2">
                                        {savedCharts.map((chart, i) => (
                                            <motion.button
                                                key={chart.id}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: i * 0.05 }}
                                                onClick={() => loadSavedChart(chart)}
                                                className="w-full flex items-center justify-between p-3 bg-purple-900/30 hover:bg-purple-800/40 border border-purple-500/20 hover:border-purple-400/40 rounded-lg transition-all text-left"
                                            >
                                                <div>
                                                    <div className="text-amber-100 font-medium">{chart.name}</div>
                                                    <div className="text-purple-300/70 text-xs">
                                                        {new Date(chart.createdAt).toLocaleDateString()}
                                                    </div>
                                                </div>
                                                <span className="text-purple-400">→</span>
                                            </motion.button>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                )}
            </div>
        );
    };

    return (
        <PageContainer className="py-8">
            <AnimatePresence mode="wait">
                {renderContent()}
            </AnimatePresence>

            {/* Planet card modal */}
            <AnimatePresence>
                {selectedPlanet && (
                    <PlanetCard
                        planet={selectedPlanet}
                        onClose={closePlanetCard}
                        onGetInsight={handleGetPlanetInsight}
                        isLoading={isLoadingPlanetInsight}
                        insight={planetInsight}
                    />
                )}
            </AnimatePresence>

            {/* Save chart modal */}
            <AnimatePresence>
                {showSaveModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    >
                        <div
                            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                            onClick={() => setShowSaveModal(false)}
                        />
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="relative w-full max-w-sm p-6 bg-gradient-to-br from-purple-900/95 via-purple-950/95 to-indigo-950/95 border border-purple-500/30 rounded-2xl"
                        >
                            <h3 className="text-xl font-serif text-amber-100 mb-4">Save Your Chart</h3>
                            <p className="text-purple-300/70 text-sm mb-4">
                                Give your chart a name (e.g., "My Chart" or a friend's name)
                            </p>
                            <input
                                type="text"
                                value={chartNameInput}
                                onChange={(e) => setChartNameInput(e.target.value)}
                                placeholder="Enter a name..."
                                className="w-full px-4 py-3 mb-4 input-theme rounded-xl focus:outline-none focus:border-theme-accent"
                            />
                            <div className="flex gap-3">
                                <Button
                                    variant="secondary"
                                    size="md"
                                    onClick={() => setShowSaveModal(false)}
                                    className="flex-1"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={handleSaveChart}
                                    loading={isSaving}
                                    icon={Save}
                                    className="flex-1"
                                >
                                    {isSaving ? 'Saving...' : 'Save'}
                                </Button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </PageContainer>
    );
};

// Big Three card component
const BigThreeCard = ({ title, sign, symbol, subtitle }) => (
    <div className="p-4 bg-theme-glass backdrop-blur-sm border border-theme-glass rounded-xl text-center">
        <div className="text-2xl mb-1" style={{ textShadow: '0 0 15px currentColor' }}>
            {symbol}
        </div>
        <div className="text-theme font-medium text-sm">{sign}</div>
        <div className="text-theme-muted text-xs mt-1">{subtitle}</div>
    </div>
);

export default BirthChartScreen;
